import React, { useEffect, useMemo, useState } from "react";
import { api, extractError } from "../../lib/api";
import { formatInr } from "../../lib/accountsSettlement";
import { cn } from "../../lib/utils";
import { SectionCard, Pill } from "../../components/Ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import CompactTableFilters from "../../components/CompactTableFilters";
import DataTablePagination from "../../components/DataTablePagination";
import VoucherDocumentActions from "../../components/VoucherDocumentActions";
import { useCashLedgerBootstrap } from "../../hooks/useBootstrapList";
import { useBootstrapWS } from "../../hooks/useBootstrapWS";
import { LIST_PAGE_SIZE_OPTIONS } from "../../lib/listPagination";
import { CASH_LEDGER_COLUMNS } from "../accountsColumns";
import { ArrowDownToLine, ArrowUpFromLine, BookOpen, Search } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const ALLOCATION_MODES = [
  { value: "VOUCHER", label: "Against voucher" },
  { value: "ON_ACCOUNT", label: "On account" },
  { value: "ADVANCE", label: "Advance" },
  { value: "LABOUR_PAYABLE", label: "Labour payable (GL)" },
  { value: "DALA_PAYABLE", label: "Dala payable (GL)" },
];

export default function AccountsCashLedgerPanel({
  canIssue,
  onChanged,
  paymentMode = "CASH",
}) {
  const isBank = String(paymentMode || "CASH").toUpperCase() === "BANK";
  const { ledger, materialTargets, partyPickers, aggregates, reload } = useCashLedgerBootstrap({
    ledgerColumns: CASH_LEDGER_COLUMNS,
    defaultSort: { id: "code", desc: true },
    enabled: true,
    paymentMode: isBank ? "BANK" : "CASH",
  });
  useBootstrapWS(["voucher", "bank_statement"], reload);

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [formTab, setFormTab] = useState("payment");

  const rows = ledger.table.rows;
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((v) =>
      [v.code, v.purchase_code, v.party_name, v.source_voucher_code].some((x) =>
        String(x || "").toLowerCase().includes(q),
      ),
    );
  }, [rows, search]);

  useEffect(() => {
    if (!rows.length) {
      setSelectedId(null);
      return;
    }
    const stillThere = rows.some((v) => v.id === selectedId);
    if (!stillThere) setSelectedId(rows[0].id);
  }, [rows, selectedId]);

  const selected = rows.find((v) => v.id === selectedId) || null;

  const afterChange = (entry) => {
    reload();
    onChanged?.();
    if (entry?.id) setSelectedId(entry.id);
  };

  const outLabel = isBank ? "Bank payment" : "Cash out";
  const inLabel = isBank ? "Bank receipt" : "Cash in";
  const queueTitle = isBank ? "Bank ledger" : "Cash ledger";
  const emptyMsg = isBank
    ? "No bank payments or receipts yet. Post from Bank reconciliation."
    : "No cash in/out entries yet.";

  return (
    <div
      className="space-y-4 min-w-0"
      data-testid={isBank ? "accounts-bank-ledger-panel" : "accounts-cash-ledger-panel"}
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={isBank ? "Bank paid" : "Cash out"} value={formatInr(aggregates.payment_total || 0)} tone="amber" />
        <Stat label={isBank ? "Bank received" : "Cash in"} value={formatInr(aggregates.receipt_total || 0)} tone="blue" />
        <Stat
          label={isBank ? "Net bank out" : "Net cash out"}
          value={formatInr(aggregates.net_cash_out || 0)}
        />
        <Stat
          label="Entries"
          value={`${aggregates.payment_count || 0} out · ${aggregates.receipt_count || 0} in`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] gap-4 items-start min-w-0">
        <div className="min-w-0 w-full lg:max-w-80 lg:sticky lg:top-20 lg:max-h-[calc(100dvh-6rem)] lg:overflow-y-auto">
          <SectionCard title={queueTitle} testId={isBank ? "bank-ledger-queue" : "cash-ledger-queue"} className="min-w-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                className="pl-8 h-8 text-xs w-full"
                placeholder="Search voucher, party…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">{emptyMsg}</p>
            ) : filteredRows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No matches on this page.</p>
            ) : (
              <div className="mt-3 space-y-1.5 max-h-[min(42vh,360px)] overflow-y-auto pr-0.5">
                {filteredRows.map((v) => (
                  <LedgerQueueItem
                    key={v.id}
                    entry={v}
                    selected={v.id === selectedId}
                    onSelect={() => setSelectedId(v.id)}
                    inLabel={isBank ? "Receipt" : "In"}
                    outLabel={isBank ? "Payment" : "Out"}
                  />
                ))}
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-border/60 space-y-2">
              <CompactTableFilters
                columns={CASH_LEDGER_COLUMNS}
                table={ledger.table}
                testId={isBank ? "bank-ledger-filters" : "cash-ledger-filters"}
              />
              <DataTablePagination
                {...ledger.pagination}
                pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
                testId={isBank ? "bank-ledger-pagination" : "cash-ledger-pagination"}
                compact
              />
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4 min-w-0">
          {canIssue && !isBank && (
            <SectionCard title="New entry" testId="cash-ledger-forms">
              <Tabs value={formTab} onValueChange={setFormTab}>
                <TabsList className="mb-3">
                  <TabsTrigger value="payment" className="text-xs gap-1">
                    <ArrowUpFromLine className="h-3 w-3" /> Cash out
                  </TabsTrigger>
                  <TabsTrigger value="receipt" className="text-xs gap-1">
                    <ArrowDownToLine className="h-3 w-3" /> Cash in
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="payment">
                  <CashEntryForm
                    kind="payment"
                    partyPickers={partyPickers}
                    targets={materialTargets}
                    canIssue={canIssue}
                    onDone={afterChange}
                  />
                </TabsContent>
                <TabsContent value="receipt">
                  <CashEntryForm
                    kind="receipt"
                    partyPickers={partyPickers}
                    targets={materialTargets}
                    canIssue={canIssue}
                    onDone={afterChange}
                  />
                </TabsContent>
              </Tabs>
            </SectionCard>
          )}

          {isBank ? (
            <SectionCard title="Bank posting" testId="bank-ledger-hint">
              <p className="text-sm text-muted-foreground">
                Bank payments and receipts are posted from{" "}
                <a href="/accounts/bank-reconciliation" className="text-primary underline-offset-2 hover:underline">
                  Bank reconciliation
                </a>{" "}
                (HDFC statement → against bills / on account / advance). This register lists those BP/BR vouchers for review and Tally sync.
              </p>
            </SectionCard>
          ) : null}

          <SectionCard
            title={
              selected
                ? `${selected.entry_type === "RECEIPT" ? inLabel : outLabel} · ${selected.code}`
                : "Entry detail"
            }
            testId={isBank ? "bank-ledger-detail" : "cash-ledger-detail"}
          >
            {!selected ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Select a ledger entry.</p>
            ) : (
              <LedgerDetail entry={selected} targets={materialTargets} canIssue={canIssue && !isBank} onReload={reload} />
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const tones = { amber: "text-amber-800", blue: "text-blue-800" };
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={cn("text-lg font-semibold tabular mt-1", tone && tones[tone])}>{value}</p>
    </div>
  );
}

function LedgerQueueItem({ entry: v, selected, onSelect, inLabel = "In", outLabel = "Out" }) {
  const isReceipt = v.entry_type === "RECEIPT";
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "w-full text-left rounded-md border px-3 py-2.5 transition-colors",
        selected
          ? isReceipt
            ? "border-blue-400 bg-blue-50/80 ring-1 ring-blue-200"
            : "border-amber-400 bg-amber-50/80 ring-1 ring-amber-200"
          : "border-border bg-card hover:bg-muted/40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-mono text-xs font-semibold">{v.code}</p>
        <Pill tone={isReceipt ? "sage" : "warning"}>{isReceipt ? inLabel : outLabel}</Pill>
      </div>
      <p className="text-xs font-medium mt-1 truncate">{v.party_name}</p>
      <p className="text-[10px] text-muted-foreground mt-0.5">
        {v.allocation_mode?.replace("_", " ") || "—"}
        {v.source_voucher_code ? ` · ${v.source_voucher_code}` : ""} · {formatInr(v.net_payable)}
      </p>
    </button>
  );
}

function CashEntryForm({ kind, partyPickers, targets, canIssue, onDone }) {
  const [partyId, setPartyId] = useState("");
  const [mode, setMode] = useState("ON_ACCOUNT");
  const [voucherId, setVoucherId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const partyTargets = useMemo(() => {
    if (!partyId) return targets;
    return targets.filter((t) => String(t.party_id) === String(partyId));
  }, [targets, partyId]);

  useEffect(() => {
    if (partyPickers.length && !partyId) setPartyId(String(partyPickers[0].id));
  }, [partyPickers, partyId]);

  useEffect(() => {
    if (mode !== "VOUCHER") {
      setVoucherId("");
      return;
    }
    if (partyTargets.length) setVoucherId(String(partyTargets[0].id));
    else setVoucherId("");
  }, [mode, partyTargets]);

  const target = partyTargets.find((t) => String(t.id) === String(voucherId));

  if (!canIssue) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No permission to post cash entries.</p>;
  }

  const submit = async () => {
    const val = Number(amount);
    if (!partyId) {
      toast.error("Select a party");
      return;
    }
    if (!val || val <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (mode === "VOUCHER" && !voucherId) {
      toast.error("Select a voucher");
      return;
    }
    if (mode === "VOUCHER" && target && val > target.balance_due + 0.01) {
      toast.error(`Amount exceeds balance due (${formatInr(target.balance_due)})`);
      return;
    }
    try {
      setBusy(true);
      const path = kind === "receipt" ? "/cash-ledger/receipt" : "/cash-ledger/payment";
      const { data } = await api.post(path, {
        party_id: Number(partyId),
        amount: val,
        allocation_mode: mode,
        voucher_id: mode === "VOUCHER" ? Number(voucherId) : undefined,
        accounts_note: note.trim() || undefined,
      });
      toast.success(`${kind === "receipt" ? "Receipt" : "Payment"} ${data.code} posted`);
      setAmount("");
      setNote("");
      onDone?.(data);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`cash-ledger-form-${kind}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Party</Label>
          <select
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card px-3 text-xs"
            value={partyId}
            onChange={(e) => setPartyId(e.target.value)}
          >
            {partyPickers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.display_name} ({p.kind})
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Allocation</Label>
          <select
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card px-3 text-xs"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            {ALLOCATION_MODES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>
      {mode === "VOUCHER" && (
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Voucher</Label>
          <select
            className="mt-1 flex h-9 w-full rounded-md border border-input bg-card px-3 text-xs"
            value={voucherId}
            onChange={(e) => setVoucherId(e.target.value)}
          >
            {partyTargets.length === 0 ? (
              <option value="">No open vouchers for this party</option>
            ) : (
              partyTargets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} · {t.voucher_type} · due {formatInr(t.balance_due)}
                </option>
              ))
            )}
          </select>
        </div>
      )}
      {target && mode === "VOUCHER" && (
        <div className="grid grid-cols-3 gap-2 text-xs rounded-md border bg-muted/20 p-3">
          <div>
            <span className="text-[10px] uppercase text-muted-foreground block">Net payable</span>
            <span className="font-semibold tabular">{formatInr(target.settlement_net)}</span>
          </div>
          <div>
            <span className="text-[10px] uppercase text-muted-foreground block">Paid / adjusted</span>
            <span className="font-semibold tabular">
              {formatInr((target.cash_paid_total || 0) + (target.receipt_applied_total || 0))}
            </span>
          </div>
          <div>
            <span className="text-[10px] uppercase text-muted-foreground block">Balance due</span>
            <span className="font-semibold tabular text-primary">{formatInr(target.balance_due)}</span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase text-muted-foreground">Amount (₹)</Label>
          <Input type="number" step="0.01" className="mt-1 h-8 text-xs" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase text-muted-foreground">Note</Label>
          <Textarea className="mt-1 text-xs min-h-[40px]" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          className={kind === "receipt" ? "bg-blue-700 hover:bg-blue-800 text-white" : "bg-amber-700 hover:bg-amber-800 text-white"}
          onClick={submit}
          disabled={busy}
        >
          {busy ? "Posting…" : kind === "receipt" ? "Receive cash" : "Issue cash"}
        </Button>
      </div>
    </div>
  );
}

function LedgerDetail({ entry, targets, canIssue, onReload }) {
  const [allocVoucherId, setAllocVoucherId] = useState("");
  const [allocAmount, setAllocAmount] = useState("");
  const [allocating, setAllocating] = useState(false);
  const isReceipt = entry.entry_type === "RECEIPT";
  const canAllocate = canIssue && entry.unallocated_amount > 0.01 && entry.allocation_mode !== "VOUCHER";

  useEffect(() => {
    if (targets.length) setAllocVoucherId(String(targets[0].id));
  }, [targets]);

  const allocate = async () => {
    const val = Number(allocAmount);
    if (!allocVoucherId || !val || val <= 0) {
      toast.error("Enter voucher and amount");
      return;
    }
    try {
      setAllocating(true);
      await api.post(`/cash-ledger/${entry.id}/allocate`, {
        voucher_id: Number(allocVoucherId),
        amount: val,
      });
      toast.success("Allocated to voucher");
      setAllocAmount("");
      onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setAllocating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className={cn("rounded-lg border p-4 text-center", isReceipt ? "border-blue-200 bg-blue-50/60" : "border-amber-200 bg-amber-50/60")}>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {isReceipt ? "Cash received" : "Cash paid"}
        </p>
        <p className={cn("text-2xl font-bold tabular mt-1", isReceipt ? "text-blue-900" : "text-amber-900")}>
          {formatInr(entry.net_payable)}
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          {entry.allocation_mode?.replace("_", " ")}
          {entry.source_voucher_code ? ` · vs ${entry.source_voucher_code}` : ""}
        </p>
        {entry.unallocated_amount > 0.01 && (
          <p className="text-xs mt-1">Unallocated: <strong className="tabular">{formatInr(entry.unallocated_amount)}</strong></p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-muted-foreground">Party</span>
          <p className="font-medium">{entry.party_name}</p>
          {entry.party_id ? (
            <Button size="sm" variant="link" className="h-auto p-0 mt-1 text-xs" asChild>
              <Link to={`/parties/${entry.party_id}/ledger`}>
                <BookOpen className="h-3 w-3 mr-1 inline" />
                View ledger
              </Link>
            </Button>
          ) : null}
        </div>
        <div><span className="text-muted-foreground">Purchase</span><p className="font-mono">{entry.purchase_code || "—"}</p></div>
      </div>
      {entry.accounts_note && <p className="text-xs text-muted-foreground border-t pt-2">{entry.accounts_note}</p>}
      {canAllocate && (
        <div className="rounded-md border p-3 space-y-2">
          <p className="text-xs font-medium">Apply on-account balance to voucher</p>
          <select
            className="flex h-8 w-full rounded-md border px-2 text-xs"
            value={allocVoucherId}
            onChange={(e) => setAllocVoucherId(e.target.value)}
          >
            {targets.map((t) => (
              <option key={t.id} value={t.id}>{t.code} · due {formatInr(t.balance_due)}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <Input
              type="number"
              step="0.01"
              className="h-8 text-xs"
              placeholder={String(entry.unallocated_amount)}
              value={allocAmount}
              onChange={(e) => setAllocAmount(e.target.value)}
            />
            <Button type="button" size="sm" variant="outline" onClick={allocate} disabled={allocating}>
              {allocating ? "…" : "Allocate"}
            </Button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 border-t pt-2">
        <VoucherDocumentActions voucherId={entry.id} voucherCode={entry.code} />
      </div>
    </div>
  );
}
